import type { ContentCreatedByFlowAttributesType } from '../../common/utils/analytics/generated/analytics.types';
import type { EventPayloadAttributes } from '../../common/utils/analytics/generated/create-event-payload';
import { ADFConversionError, PageTitleError, UserPermissionError } from '../../common/utils/errors';

import type { CreateConfluencePageSuccessResponse } from './types';
import { getCreateConfluencePageURL } from './utils';

type ContentCreatedByFlowEventKey = keyof ContentCreatedByFlowEvent;
type ContentCreatedByFlowEvent = {
	'track.content.createdByFlow': ContentCreatedByFlowAttributesType;
};
type CreateEventPayload = <K extends ContentCreatedByFlowEventKey>(
	eventKey: ContentCreatedByFlowEventKey,
	...[attributes]: EventPayloadAttributes<K>
) => void;

export const createConfluencePage = async ({
	title,
	spaceId,
	parentId,
	cloudId,
	gatewayBaseUrl = '',
	pageBody = '',
	status = 'current',
	representation = 'storage',
	subtype,
	fireAnalyticsEvent,
	creationFlow,
}: {
	title: string;
	spaceId: string;
	cloudId: string;
	parentId?: string;
	pageBody?: string;
	gatewayBaseUrl?: string;
	status?: 'current' | 'draft';
	representation?: 'storage' | 'atlas_doc_format';
	subtype?: string;
	fireAnalyticsEvent?: (...params: Parameters<CreateEventPayload>) => void;
	creationFlow?: string;
}): Promise<CreateConfluencePageSuccessResponse> => {
	/** API docs at
	 * https://developer.atlassian.com/cloud/confluence/rest/v2/api-group-page/#api-pages-post
	 */
	const response = await fetch(getCreateConfluencePageURL({ cloudId, gatewayBaseUrl }), {
		method: 'post',
		credentials: 'include',
		headers: {
			accept: 'application/json',
			'Content-Type': 'application/json',
		},
		body: JSON.stringify({
			status,
			title,
			spaceId,
			...(parentId && { parentId }),
			body: {
				representation,
				value: pageBody,
			},
			...(subtype && { subtype }),
		}),
	});

	if (!response.ok) {
		switch (response.status) {
			case 404: // Permission denied
				throw new UserPermissionError(
					'The user does not have the required permission for this action.',
				);

			// 400 is a catch-all error from the Create Page API
			case 400:
				let errorMessage = '';
				try {
					// note: response can only be consumed once
					errorMessage = (await response.json()).errors?.[0]?.title;
				} catch (error) {}

				if (errorMessage.includes('Error converting ADF to storage format')) {
					throw new ADFConversionError('Error converting ADF to storage format');
				}

				// Invalid Content type
				throw new PageTitleError('A page with this title already exists');
			default:
				throw response;
		}
	} else {
		fireAnalyticsEvent?.('track.content.createdByFlow', {
			flow: `linkCreateConfluence-${creationFlow}`,
			contentType: 'page',
			isLivePage: subtype === 'live',
			collabService: 'ncs',
		});
	}

	return response.json();
};
